import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BRAND_COLORS } from '@lk/shared';
import { Button } from '../../components/Button';
import { Icon, SUBJECT_ICON } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { EmptyState, ErrorState, LoadingState } from '../../components/States';
import { formatDate } from '../../lib/format';
import { humanMessage } from '../../lib/errors';
import { submitReviewDecision, type ReviewQueueItem } from '../../lib/api';
import { reviewQueueQuery } from '../learner/queries';
import { QuestionText } from '../../components/QuestionText';
import { FigureFailures } from './FigureFailures';

/**
 * Черга модерації питань, згенерованих LLM.
 * Нічого не потрапляє до дітей без явного «схвалити» від батька.
 */
export function ReviewPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const queue = useQuery({ ...reviewQueueQuery(), retry: false });

  const decide = useMutation({
    mutationFn: submitReviewDecision,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewQueueQuery().queryKey });
    },
  });

  if (queue.isLoading) return <LoadingState label="Завантажуємо чергу…" />;
  if (queue.isError) return <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />;

  const items = queue.data ?? [];

  // Поламані малюнки показуються НАД чергою і незалежно від неї: порожня
  // черга модерації — звичайний стан, і ховати за нею повідомлення про
  // нерозвʼязне питання означало б знову зробити поломку невидимою.
  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <FigureFailures />
        <EmptyState
          icon="correct"
          title="Черга порожня"
          hint="Усі згенеровані питання опрацьовані."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <FigureFailures />
      <Panel className="p-4">
        <h2 className="text-lg font-black">Модерація питань · {items.length} у черзі</h2>
        <p className="mt-1 text-xs font-semibold text-white/55">
          Кожне питання вже пройшло автоматичні перевірки. Твоє слово — останнє.
        </p>
        {decide.isError ? (
          <p className="mt-2 text-sm font-bold text-[var(--lk-red)]" role="alert">
            {humanMessage(decide.error)}
          </p>
        ) : null}
      </Panel>

      {items.map((item) => (
        <ReviewCard
          key={item.id}
          item={item}
          busy={decide.isPending}
          onDecide={(decision, patch, reason) =>
            decide.mutate({
              itemId: item.id,
              decision,
              ...(patch ? { patch } : {}),
              ...(reason ? { reason } : {}),
            })
          }
        />
      ))}
    </div>
  );
}

function ReviewCard({
  item,
  busy,
  onDecide,
}: {
  item: ReviewQueueItem;
  busy: boolean;
  onDecide: (
    decision: 'approve' | 'reject' | 'edit',
    patch?: { prompt?: string; explanation?: string },
    reason?: string,
  ) => void;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [prompt, setPrompt] = useState(item.prompt);
  const [explanation, setExplanation] = useState(item.explanation);
  const [reason, setReason] = useState('');

  const failed = item.validations.filter((check) => !check.passed);

  return (
    <Panel className="p-4" style={{ borderColor: failed.length > 0 ? `${BRAND_COLORS.red}66` : undefined }}>
      <header className="flex flex-wrap items-center gap-2 text-[11px] font-black text-white/50 uppercase">
        <Icon name={SUBJECT_ICON[item.subject]} size={16} />
        <span>{item.skillId}</span>
        <span className="text-white/30">·</span>
        <span>{item.format}</span>
        <span className="text-white/30">·</span>
        <span>складність {item.difficulty.toFixed(0)}</span>
        <div className="flex-1" />
        <span>{formatDate(item.createdAt)}</span>
      </header>

      {editing ? (
        <div className="mt-3 flex flex-col gap-2">
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            rows={3}
            className="lk-panel-tight w-full p-3 text-sm font-bold outline-none"
            aria-label="Текст питання"
          />
          <textarea
            value={explanation}
            onChange={(event) => setExplanation(event.target.value)}
            rows={3}
            className="lk-panel-tight w-full p-3 text-sm font-semibold outline-none"
            aria-label="Пояснення"
          />
        </div>
      ) : (
        <>
          <p className="mt-3 text-base font-black">
            <QuestionText text={item.prompt} />
          </p>
          {item.choices && item.choices.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {item.choices.map((choice) => (
                <li key={choice.id} className="lk-chip text-[11px]">
                  {choice.text}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="mt-2 text-sm font-bold text-[var(--lk-green)]">
            відповідь: {item.answerText}
          </p>
          <p className="mt-1.5 text-xs leading-relaxed font-semibold text-white/70">
            {item.explanation}
          </p>
        </>
      )}

      {item.hints.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1 text-[11px] font-semibold text-white/50">
          {item.hints.map((hint, index) => (
            <li key={index} className="flex items-start gap-1.5">
              <Icon name="hint" size={14} className="mt-0.5" />
              <span className="min-w-0 flex-1">{hint}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {item.validations.map((check) => (
          <span
            key={check.check}
            className="lk-chip text-[10px]"
            style={{
              borderColor: check.passed ? BRAND_COLORS.green : BRAND_COLORS.red,
              color: check.passed ? BRAND_COLORS.green : BRAND_COLORS.red,
            }}
            title={check.detail}
          >
            <Icon
              name={check.passed ? 'correct' : 'wrong'}
              size={12}
              label={check.passed ? 'перевірка пройдена' : 'перевірка не пройдена'}
            />
            {check.check}
          </span>
        ))}
      </div>

      {item.nearestDuplicate ? (
        <p className="mt-2 text-[11px] font-bold text-[var(--lk-gold)]">
          Схоже на наявне ({Math.round(item.nearestDuplicate.similarity * 100)}%):{' '}
          <span className="text-white/70">
            <QuestionText text={item.nearestDuplicate.prompt} />
          </span>
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          tone="green"
          mute
          disabled={busy}
          onClick={() =>
            editing
              ? onDecide('edit', { prompt, explanation })
              : onDecide('approve')
          }
        >
          {editing ? 'Зберегти й схвалити' : 'Схвалити'}
        </Button>
        <Button tone="ghost" mute disabled={busy} onClick={() => setEditing((v) => !v)}>
          {editing ? 'Скасувати правку' : 'Виправити'}
        </Button>
        <input
          type="text"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="причина відхилення"
          className="lk-panel-tight h-11 min-w-[10rem] flex-1 px-3 text-sm font-bold outline-none"
          aria-label="Причина відхилення"
        />
        <Button tone="red" mute disabled={busy} onClick={() => onDecide('reject', undefined, reason)}>
          Відхилити
        </Button>
      </div>
    </Panel>
  );
}
